import type { CSSProperties } from 'react'

/**
 * Shared colors for ticket status and priority badges.
 */

type Pair = { light: CSSProperties; dark: CSSProperties }

const STATUS: Record<string, Pair> = {
  new: {
    light: { backgroundColor: '#FEF3C7', color: '#D97706' },
    dark: { backgroundColor: '#78350F', color: '#FCD34D' },
  },
  open: {
    light: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    dark: { backgroundColor: '#7F1D1D', color: '#FCA5A5' },
  },
  in_progress: {
    light: { backgroundColor: '#DBEAFE', color: '#2563EB' },
    dark: { backgroundColor: '#1E3A8A', color: '#93C5FD' },
  },
  pending: {
    light: {
      backgroundColor: '#1F2937',
      color: '#FFFFFF',
      textShadow: '0 0 2px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(0, 0, 0, 0.7)',
    },
    dark: { backgroundColor: '#111827', color: '#F9FAFB' },
  },
  on_hold: {
    light: { backgroundColor: '#F3F4F6', color: '#6B7280' },
    dark: { backgroundColor: '#374151', color: '#D1D5DB' },
  },
  emergency: {
    light: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    dark: { backgroundColor: '#7F1D1D', color: '#FCA5A5' },
  },
  resolved: {
    light: { backgroundColor: '#D4F4E8', color: '#4EC9A6' },
    dark: { backgroundColor: '#1A3D35', color: '#5EC9B0' },
  },
  closed: {
    light: { backgroundColor: '#F1F3F5', color: '#5C6B7A' },
    dark: { backgroundColor: '#353640', color: '#B4B6BA' },
  },
  spam: {
    light: { backgroundColor: '#F1F3F5', color: '#5C6B7A' },
    dark: { backgroundColor: '#353640', color: '#B4B6BA' },
  },
}

const PRIORITY: Record<string, Pair> = {
  urgent: {
    light: { backgroundColor: '#FEE2E2', color: '#E87A7A' },
    dark: { backgroundColor: '#4A2A2A', color: '#E87A7A' },
  },
  high: {
    light: { backgroundColor: '#FED7AA', color: '#EA580C' },
    dark: { backgroundColor: '#7C2D12', color: '#FDBA74' },
  },
  normal: {
    light: { backgroundColor: '#E8EBF5', color: '#6C7AE0' },
    dark: { backgroundColor: '#3A3D5C', color: '#7B8AE8' },
  },
  low: {
    light: { backgroundColor: '#F1F3F5', color: '#5C6B7A' },
    dark: { backgroundColor: '#353640', color: '#B4B6BA' },
  },
}

const FALLBACK_STATUS: Pair = STATUS.closed
const FALLBACK_PRIORITY: Pair = PRIORITY.normal

export function classicStatusBadgeRootStyle(status: string | null | undefined, isDark: boolean): CSSProperties {
  const s = String(status ?? '')
    .toLowerCase()
    .trim()
  const pair = STATUS[s] ?? FALLBACK_STATUS
  return { ...(isDark ? pair.dark : pair.light) }
}

export function classicPriorityBadgeRootStyle(
  priority: string | null | undefined,
  isDark: boolean,
): CSSProperties {
  const p = String(priority ?? 'normal')
    .toLowerCase()
    .trim()
  const pair = PRIORITY[p] ?? FALLBACK_PRIORITY
  return { ...(isDark ? pair.dark : pair.light) }
}
